import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Check, CheckCircle2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  USERNAME_MAX,
  USERNAME_MIN,
  checkUsernameAvailable,
  validateUsernameFormat,
} from "@/lib/username";
import { track } from "@/lib/analyticsEvents";
import { cn } from "@/lib/utils";
import { getStripeEnvironment } from "@/lib/stripe";
import { PAID_TIERS, planFor, type Billing, type PaidTier } from "@/lib/signupPricing";
import { TIER_PRICING, TRIAL_DAYS } from "@/lib/tier";

const emailSchema = z.string().trim().min(1, "Email is required").email("Enter a valid email address").max(255);
const signUpSchema = z.object({
  displayName: z.string().trim().min(1, "Display name is required").max(100),
  username: z.string().trim().min(USERNAME_MIN).max(USERNAME_MAX),
  email: emailSchema,
});

interface Props {
  initialEmail: string;
  initialTier: PaidTier;
  initialBilling: Billing;
  onCheckoutReady: (clientSecret: string) => void;
  onAlreadyRegistered: () => void;
}

export function SignUpForm({ initialEmail, initialTier, initialBilling, onCheckoutReady, onAlreadyRegistered }: Props) {
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [signupEmail, setSignupEmail] = useState(initialEmail);
  const [usernameStatus, setUsernameStatus] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [tier, setTier] = useState<PaidTier>(initialTier);
  const [billing, setBilling] = useState<Billing>(initialBilling);
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<"displayName" | "username" | "email", string>>>({});
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    if (username.length === 0) { setUsernameStatus("idle"); return; }
    const v = validateUsernameFormat(username);
    if (!v.ok) { setUsernameStatus("invalid"); return; }
    setUsernameStatus("checking");
    const handle = window.setTimeout(async () => {
      const ok = await checkUsernameAvailable(v.normalized);
      setUsernameStatus(ok ? "available" : "taken");
    }, 350);
    return () => window.clearTimeout(handle);
  }, [username]);

  const startCheckout = async () => {
    setSignUpError(null);
    if (!acceptedTerms) {
      toast.error("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    const usernameValidation = validateUsernameFormat(username);
    const parsed = signUpSchema.safeParse({ displayName, username, email: signupEmail });
    if (!parsed.success || !usernameValidation.ok) {
      const fe = parsed.success ? {} : parsed.error.flatten().fieldErrors;
      setErrors({
        displayName: fe.displayName?.[0],
        username: !usernameValidation.ok
          ? (usernameValidation as { ok: false; message: string }).message
          : fe.username?.[0],
        email: fe.email?.[0],
      });
      return;
    }
    if (usernameStatus === "taken") {
      setErrors((p) => ({ ...p, username: "That username is taken." }));
      return;
    }
    setErrors({});
    setPreparing(true);
    track("signup_started", { tier, billing });

    const { data, error } = await supabase.functions.invoke("signup-prepare", {
      body: {
        email: signupEmail.trim().toLowerCase(),
        desiredUsername: usernameValidation.normalized,
        displayName: displayName.trim(),
        priceId: `${tier}_${billing}`,
        environment: getStripeEnvironment(),
        returnUrl: `${window.location.origin}/auth/finalize?session_id={CHECKOUT_SESSION_ID}`,
      },
    });
    setPreparing(false);
    if (error || data?.error) {
      setSignUpError(error?.message ?? data?.error ?? "Could not start signup.");
      return;
    }
    if (data?.alreadyRegistered) {
      onAlreadyRegistered();
      return;
    }
    if (!data?.clientSecret) {
      setSignUpError("Stripe did not return a checkout session.");
      return;
    }
    onCheckoutReady(data.clientSecret as string);
    track("signup_card_captured", { tier, billing });
  };

  const selectedPrice = TIER_PRICING[tier][billing];

  return (
    <form onSubmit={(e) => { e.preventDefault(); void startCheckout(); }} className="space-y-5" noValidate>
      {signUpError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unable to start signup</AlertTitle>
          <AlertDescription>{signUpError}</AlertDescription>
        </Alert>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Choose your plan</Label>
          <div className="flex items-center gap-2">
            <span className={cn("text-xs", billing === "monthly" ? "text-foreground" : "text-muted-foreground")}>Monthly</span>
            <Switch checked={billing === "yearly"} onCheckedChange={(c) => setBilling(c ? "yearly" : "monthly")} aria-label="Billing period" />
            <span className={cn("text-xs", billing === "yearly" ? "text-foreground" : "text-muted-foreground")}>Yearly</span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {PAID_TIERS.map((t) => {
            const opt = planFor(t, billing);
            const selected = t === tier;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTier(t)}
                aria-pressed={selected}
                className={cn(
                  "rounded-lg border p-2 text-left transition",
                  selected ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-border hover:border-primary/40",
                )}
              >
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-muted-foreground">${opt.amount}{billing === "monthly" ? "/mo" : "/yr"}</div>
                {selected && <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-1" />}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {TRIAL_DAYS}-day free trial on every plan. Card required at signup; nothing charged until day {TRIAL_DAYS + 1}.
        </p>
      </div>

      <div>
        <Label htmlFor="signup-display-name">Display name</Label>
        <Input id="signup-display-name" autoComplete="name" value={displayName}
          onChange={(e) => { setDisplayName(e.target.value); setSignUpError(null); setErrors((p) => ({ ...p, displayName: undefined })); }}
          placeholder="Your name" disabled={preparing}
          aria-invalid={errors.displayName ? "true" : "false"} />
        {errors.displayName && <p className="mt-1 text-sm text-destructive">{errors.displayName}</p>}
      </div>

      <div>
        <Label htmlFor="signup-username">Username</Label>
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">@</span>
          <Input id="signup-username" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
            value={username}
            onChange={(e) => { setUsername(e.target.value.toLowerCase().slice(0, USERNAME_MAX)); setSignUpError(null); setErrors((p) => ({ ...p, username: undefined })); }}
            placeholder="yourhandle" disabled={preparing}
            className="pl-7 pr-9"
            aria-invalid={errors.username ? "true" : "false"} />
          <span className="absolute inset-y-0 right-3 flex items-center">
            {usernameStatus === "checking" ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              : usernameStatus === "available" ? <Check className="h-4 w-4 text-primary" />
              : (usernameStatus === "taken" || usernameStatus === "invalid") ? <X className="h-4 w-4 text-destructive" />
              : null}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Your handle is your referral link and is reserved for your future profile. Can't be changed later.
        </p>
        {errors.username && <p className="mt-1 text-sm text-destructive">{errors.username}</p>}
      </div>

      <div>
        <Label htmlFor="signup-email">Email</Label>
        <Input id="signup-email" type="email" autoComplete="email" required value={signupEmail}
          onChange={(e) => { setSignupEmail(e.target.value); setSignUpError(null); setErrors((p) => ({ ...p, email: undefined })); }}
          disabled={preparing}
          aria-invalid={errors.email ? "true" : "false"} />
        {errors.email && <p className="mt-1 text-sm text-destructive">{errors.email}</p>}
      </div>

      <div className="flex items-start gap-2">
        <Checkbox id="accept-terms" checked={acceptedTerms} onCheckedChange={(v) => setAcceptedTerms(v === true)} disabled={preparing} className="mt-1" />
        <Label htmlFor="accept-terms" className="text-sm font-normal leading-snug text-muted-foreground">
          I agree to the{" "}
          <Link to="/terms/" target="_blank" className="text-primary underline">Terms of Service</Link>
          {" "}and{" "}
          <Link to="/privacy/" target="_blank" className="text-primary underline">Privacy Policy</Link>.
        </Label>
      </div>

      <Button type="submit" className="w-full" disabled={preparing || !acceptedTerms || usernameStatus !== "available"}>
        {preparing ? "Preparing checkout…" : `Continue to checkout — $${selectedPrice}${billing === "monthly" ? "/mo" : "/yr"}`}
      </Button>
      <p className="text-xs text-center text-muted-foreground">
        You'll add your card on the next step. No password — we email you a sign-in link after checkout.
      </p>
    </form>
  );
}
